import React, { useEffect, useState } from 'react';
import { FormikValues, useFormikContext } from 'formik';
import { useLanguageContext } from '@hooks/useLanguageContext';
import { SelectField } from '@components/Form/SelectField';
import { useFetchAllPhysiciansQuery } from '@api/physicians/physiciansAPI';
import { useFetchAllLocationsWithoutPaginationQuery } from '@api/locations/locationsAPI';
import { useModal } from '@components/Modal/context/useModalContext';
import { DateField } from '@components/Form/DateField';
import { CheckboxField } from '@components/Form/CheckboxField';
import { IOptions } from '@components/Select/types';
import { WarningMessage } from '@components/WarningMessage';
import { InfoVariants } from '@components/Info/types';
import { ButtonVariants } from '@components/Button/types';
import { intervalsGroup } from '@constants/options/timeOptions';
import { subtractOneMinute } from '@helpers/subtractOneMinute';
import { useScheduleContext } from '@pages/Schedule/context/useScheduleContext';
import { BlackoutType } from '@pages/Schedule/components/Blackout/type';

import './styles.scss';

interface IBlackoutComponentsProps {
  blackoutType: BlackoutType;
  isEditForm?: boolean;
  onSubmitFormForce: (formValues: FormikValues, closeModal: () => void) => void;
}

export const BlackoutComponents: React.FC<IBlackoutComponentsProps> = ({
  blackoutType,
  isEditForm = false,
  onSubmitFormForce,
}) => {
  const { values: blackoutFormValues, setFieldValue }: FormikValues = useFormikContext();

  const { closeModal } = useModal();

  const schedule = useScheduleContext();

  const { blackoutErrorMessage, setBlackoutErrorMessage } = schedule;

  const {
    physicianId = '',
    to = '',
    to_time: toTime = '',
    from = '',
    from_time: fromTime = '',
    acceptTerms = false,
  } = blackoutFormValues;

  const { dictionary } = useLanguageContext();

  const { data: physicians } = useFetchAllPhysiciansQuery();

  const { data: locations } = useFetchAllLocationsWithoutPaginationQuery();

  const locationsOptions = locations?.data?.results.map(({ name: label, id: value }) => ({
    label,
    value,
  }));

  const [filteredLocations, setFilteredLocations] = useState(locationsOptions || []);

  const physiciansOptions = physicians?.data?.results.map(({ name: label, id: value }) => ({
    label,
    value,
  }));

  const timeOptions = intervalsGroup('', '', 60);

  const lastIndex = timeOptions.length - 1;

  timeOptions[lastIndex].label = subtractOneMinute(timeOptions[lastIndex].label);
  timeOptions[lastIndex].value = subtractOneMinute(timeOptions[lastIndex].value);

  const selectedPhysiciansOptions = physiciansOptions?.find(({ value }) => value === physicianId);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setFieldValue('acceptTerms', isChecked);

    if (isChecked) {
      setFieldValue('from', from);
      setFieldValue('from_time', timeOptions[0].value);
      setFieldValue('to', to || from);
      setFieldValue('to_time', timeOptions[lastIndex].value);
    } else {
      setFieldValue('from', from);
      setFieldValue('from_time', timeOptions[0].value);
      setFieldValue('to', to);
      setFieldValue('to_time', timeOptions[lastIndex].value);
    }
  };

  useEffect(() => {
    if (physicians?.data?.results) {
      const physiciansSelected = physicians?.data.results.find(({ id }) => id === physicianId);

      const locationId = physiciansSelected?.schedule?.locationId;

      const matchedLocations = locationsOptions?.filter(({ value }) => value === locationId) || [];

      setFilteredLocations(matchedLocations);

      if (matchedLocations.length > 0) {
        setFieldValue('locationId', matchedLocations[0].value);
      }
    }
  }, [physicianId, physicians]);

  useEffect(() => {
    if (fromTime === timeOptions[0].value && toTime === timeOptions[lastIndex].value) {
      setFieldValue('acceptTerms', true);
    }
  }, [fromTime, toTime, setFieldValue]);

  useEffect(() => {
    setBlackoutErrorMessage('');
  }, [blackoutFormValues]);

  return (
    <div className="blackout-container">
      <div className="row margin">
        {blackoutType === BlackoutType.physician ? (
          <>
            <div className="col-xs-12">
              <SelectField
                selectOptions={physiciansOptions}
                value={selectedPhysiciansOptions?.label}
                placeholder={dictionary.placeholders.choosePhysician}
                label="Physician's name (PI)"
                name="physicianId"
                disabled={isEditForm}
                required
              />
            </div>
            <div className="col-xs-12">
              <SelectField
                selectOptions={filteredLocations as IOptions[]}
                placeholder={dictionary.placeholders.chooseLocation}
                label="Location name"
                name="locationId"
                disabled={isEditForm}
                required
              />
            </div>
          </>
        ) : (
          <div className="col-xs-12">
            <SelectField
              selectOptions={locationsOptions as IOptions[]}
              placeholder={dictionary.placeholders.chooseLocation}
              label="Location name"
              name="locationId"
              disabled={isEditForm}
              required
            />
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-xs-12">
          <div className="container-data-time container-with-checkbox">
            <div className="date-field">
              <DateField
                name="from"
                type="text"
                value={from}
                calendarStartDay
                placeholder="MM/DD/YYYY"
                label="Start day of blackout"
                maxDate={false}
                required
              />
            </div>
            <div className="time-field">
              <SelectField
                name="from_time"
                value={!acceptTerms ? fromTime : timeOptions[0].value}
                selectOptions={timeOptions}
                placeholder={
                  !acceptTerms ? dictionary.placeholders.chooseTime : timeOptions[0].label
                }
                label={dictionary.appointments.table.time}
                disabled={acceptTerms}
                required
              />
            </div>
            <div className="checkbox-container">
              <CheckboxField
                name="acceptTerms"
                label="All day"
                checked={acceptTerms}
                handleChange={handleCheckboxChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <div className="container-data-time">
            <div className="date-field">
              <DateField
                name="to"
                value={!acceptTerms ? to : from}
                type="text"
                placeholder="MM/DD/YYYY"
                label="Last day of blackout"
                maxDate={false}
                calendarStartDay
                required
              />
            </div>
            <div className="time-field">
              <SelectField
                name="to_time"
                value={!acceptTerms ? toTime : timeOptions.slice(-1)[0].value}
                selectOptions={timeOptions}
                placeholder={
                  !acceptTerms ? dictionary.placeholders.chooseTime : timeOptions.slice(-1)[0].label
                }
                label={dictionary.appointments.table.time}
                disabled={acceptTerms}
                required
              />
            </div>
          </div>
        </div>
      </div>
      {!!blackoutErrorMessage && (
        <WarningMessage
          title={dictionary.blackout.modals.errorMessage.title}
          buttonVariants={ButtonVariants.danger}
          buttonLabel="Schedule anyway"
          variant={InfoVariants.warning}
          onButtonClick={() => onSubmitFormForce(blackoutFormValues, closeModal)}
          message={`${blackoutType === BlackoutType.physician ? dictionary.blackout.modals.errorMessage.physicianScheduled : dictionary.blackout.modals.errorMessage.locationScheduled} ${blackoutErrorMessage}`}
        />
      )}
    </div>
  );
};
